import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import { resolveTargetAspectRatio } from '@src/hooks/useReframeSpeakersTab';
import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { ok, type Result } from '@src/services/common/Result';
import { mapFaceToSpeaker } from '@src/services/framing/mapFaceToSpeaker';
import { planSceneReframe } from '@src/services/framing/planSceneReframe';
import {
  faceLabelsInCrop,
  type NormalizedRect,
  speakerForFace,
} from '@src/services/framing/sceneFaceAssignment';
import { SmartFramingService } from '@src/services/framing/SmartFramingService';
import { SceneService } from '@src/services/SceneService';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchSpeakerVoiceMappings } from '@src/utility/speakerUtils';
import { useEffect, useMemo, useState } from 'react';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { IScene } from '@src/editor/models/layout/types';

const logger = LogManager.getLogger('feature:useUnmappedSpeakerApply');

/** The scene's speaker has no face yet, and the crop holds an unmapped one. */
export interface UnmappedSpeakerOffer {
  speakerLabel: string;
  speakerName: string;
  /** Linked to a Dato speaker; if not, one is assigned before mapping. */
  isNamed: boolean;
  faceLabel: string;
}

/**
 * The Framing tab's "Apply to {Speaker}'s Unmapped Scenes" (PLAT-11992),
 * bound to one editor.
 */
export interface UnmappedSpeakerController {
  /** What to offer for this crop on this scene, or null for nothing. */
  describe(sceneId: string, crop: NormalizedRect): UnmappedSpeakerOffer | null;
  /** Records that an unassigned transcript speaker was just linked. */
  linkSpeaker(speakerLabel: string, datoSpeakerId: string): void;
  /**
   * Saves the face as the speaker's, then plans the speaker's other scenes
   * that fell back for want of a mapping. Hand-framed scenes are left alone.
   */
  plan(
    sceneId: string,
    offer: UnmappedSpeakerOffer,
  ): Promise<
    Result<{
      sceneUpdates: Array<{ sceneId: string; updates: Partial<IScene> }>;
      speakerMap: Record<string, string[]>;
    }>
  >;
  commitMap(map: Record<string, string[]>): void;
}

/**
 * Null when there is nothing to offer from: no editor, no face data loaded
 * with the story, or the `basic` framing kill-switch. Uses the face data and
 * speaker→face map already in memory; nothing is fetched until `plan`.
 */
export function useUnmappedSpeakerApply(
  editor: IVideoEditor | undefined,
  aspectRatio: string,
): UnmappedSpeakerController | null {
  const rootStore = useStore();
  const { speakerFaceMapStore } = rootStore;
  const storyId = useVideoCreatorStore().story?.id;
  const { getSpeakerName } = useSpeakerLabels();
  const isBasic =
    rootStore.featureFlagsStore.flags.autoFramingStrategy === 'basic';

  // Already cached by the transcript's speaker labels; only says whether a
  // transcript speaker has a Dato speaker yet.
  const [voiceMappings, setVoiceMappings] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    fetchSpeakerVoiceMappings(storyId)
      .then(result => {
        if (!cancelled) setVoiceMappings(result?.mappings ?? {});
      })
      .catch(error =>
        logger.warn('Failed to read speaker mappings', extractErrorInfo(error)),
      );
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  const faceDetections = speakerFaceMapStore.faceDetections;
  const realMap = speakerFaceMapStore.map;

  return useMemo(() => {
    if (!editor || !storyId || isBasic || !faceDetections?.length) return null;
    const framer = new SmartFramingService(
      editor,
      new SceneService(),
      new LayoutEngine(),
    );
    const detections = [...faceDetections];
    const targetAspectRatio = resolveTargetAspectRatio(aspectRatio);
    const sceneInfoFor = (sceneId: string) =>
      framer.getSceneInfos().find(({ scene }) => scene.id === sceneId);

    return {
      describe(sceneId, crop) {
        const sceneInfo = sceneInfoFor(sceneId);
        if (!sceneInfo) return null;
        const speakerLabel = framer.computeSpeakerContextFraming(
          sceneInfo,
          detections,
          {},
          targetAspectRatio,
        )?.topSpeaker;
        if (!speakerLabel || realMap?.[speakerLabel]?.length) return null;

        const [faceLabel] = faceLabelsInCrop(
          framer.getSceneFaceDetections(sceneInfo, detections),
          crop,
        );
        // A face mapped to someone else is likely a deliberate reaction shot.
        if (!faceLabel || speakerForFace(realMap, faceLabel)) return null;

        return {
          speakerLabel,
          speakerName: getSpeakerName(speakerLabel),
          isNamed: !!voiceMappings[speakerLabel],
          faceLabel,
        };
      },
      linkSpeaker(speakerLabel, datoSpeakerId) {
        setVoiceMappings(previous => ({
          ...previous,
          [speakerLabel]: datoSpeakerId,
        }));
      },
      async plan(sceneId, offer) {
        const before =
          speakerFaceMapStore.mapForFraming(editor.getOriginalTranscript()) ??
          {};
        const mapped = await mapFaceToSpeaker({
          storyId,
          faceLabel: offer.faceLabel,
          speakerLabel: offer.speakerLabel,
          datoSpeakerId: voiceMappings[offer.speakerLabel],
        });
        logger.info('Mapping the framed face to the scene speaker', {
          sceneId,
          offer,
          previousMap: before,
          mappedMap: mapped.ok ? mapped.value : null,
          error: mapped.ok ? undefined : mapped.error,
        });
        if (!mapped.ok) return mapped;
        const sceneUpdates = planSceneReframe({
          framer,
          faceDetections: detections,
          speakerMap: mapped.value,
          speakerLabels: new Set([offer.speakerLabel]),
          targetAspectRatio,
          onlyListedSpeakers: true,
          excludeSceneIds: new Set([sceneId]),
          fallbackOnlyFrom: before,
        });
        return ok({ sceneUpdates, speakerMap: mapped.value });
      },
      commitMap(map) {
        speakerFaceMapStore.set(storyId, map);
      },
    };
  }, [
    editor,
    storyId,
    isBasic,
    faceDetections,
    realMap,
    aspectRatio,
    voiceMappings,
    getSpeakerName,
    speakerFaceMapStore,
  ]);
}
